import {
    useEffect,
    useState,
    useCallback
} from 'react';

import {
    Box,
    Button,
    IconButton,
    Stack,
    Typography,
    Snackbar,
    Alert
} from '@mui/material';

import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';

import { DataGrid } from '@mui/x-data-grid';

import {
    getPrintingProcesses
} from '../../services/PrintingProcessService';

import PrintingProcessCreate from './PrintingProcessCreate';
import PrintingProcessUpdate from './PrintingProcessUpdate';
import PrintingProcessDelete from './PrintingProcessDelete';

const PrintingProcessList = () => {

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const [openCreate, setOpenCreate] = useState(false);
    const [openUpdate, setOpenUpdate] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);

    const [selectedId, setSelectedId] = useState(null);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    /**
     * Show message
     */
    const showMessage = (
        message,
        severity = 'success'
    ) => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    /**
     * Load all records
     */
    const loadData = useCallback(async () => {

        try {

            setLoading(true);

            const response =
                await getPrintingProcesses();

            if (response.data?.status) {

                const data =
                    response.data.data || [];

                setRows(
                    data.map(item => ({
                        ...item,
                        id: item.id
                    }))
                );

            } else {

                setRows([]);
            }

        } catch (error) {

            console.error(
                'Get Painting Processes Error:',
                error
            );

            setRows([]);

            showMessage(
                error.response?.data?.message ||
                'Failed to load Painting Processes.',
                'error'
            );

        } finally {

            setLoading(false);
        }

    }, []);

    /**
     * Initial load
     */
    useEffect(() => {

        loadData();

    }, [loadData]);

    /**
     * Add
     */
    const handleAdd = () => {
        setOpenCreate(true);
    };

    /**
     * Edit
     */
    const handleEdit = (id) => {

        setSelectedId(id);
        setOpenUpdate(true);
    };

    /**
     * Delete
     */
    const handleDelete = (id) => {

        setSelectedId(id);
        setOpenDelete(true);
    };

    /**
     * After Create
     */
    const handleCreateSuccess = () => {

        setOpenCreate(false);

        loadData();

        showMessage(
            'Printing Process created successfully.'
        );
    };

    /**
     * After Update
     */
    const handleUpdateSuccess = () => {

        setOpenUpdate(false);
        setSelectedId(null);

        loadData();

        showMessage(
            'Painting Process updated successfully.'
        );
    };

    /**
     * After Delete
     */
    const handleDeleteSuccess = () => {

        setOpenDelete(false);
        setSelectedId(null);

        loadData();

        showMessage(
            'Painting Process deleted successfully.'
        );
    };

    /**
     * DataGrid columns
     */
    const columns = [

        {
            field: 'id',
            headerName: 'ID',
            width: 80
        },

        {
            field: 'printed_quantity',
            headerName: 'Paint Quantity',
            width: 160
        },

        {
            field: 'rejected_quantity',
            headerName: 'Rejected Quantity',
            width: 170
        },

        {
            field: 'production_date',
            headerName: 'Production Date',
            width: 160,

            valueGetter: (_, row) => {

                const value =
                    row?.production_date;

                return value
                    ? String(value).substring(0, 10)
                    : '';
            }
        },

        {
            field: 'actions',
            headerName: 'Actions',
            width: 130,
            sortable: false,
            filterable: false,

            renderCell: (params) => (

                <Stack
                    direction="row"
                    spacing={0.5}
                >

                    <IconButton hidden
                        size="small"
                        color="primary"
                        onClick={() =>
                            handleEdit(params.row.id)
                        }
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>

                    <IconButton hidden
                        size="small"
                        color="error"
                        onClick={() =>
                            handleDelete(params.row.id)
                        }
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>

                </Stack>
            )
        }
    ];

    return (

        <Box sx={{ p: 1 }}>

            {/* Header */}

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                }}
            >

                <Typography
                    variant="h5"
                    fontWeight="600"
                >
                    Printing Process
                </Typography>

                <Stack
                    direction="row"
                    spacing={1}
                >

                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={loadData}
                        disabled={loading}
                    >
                        Refresh
                    </Button>

                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAdd}
                    >
                        Add
                    </Button>

                </Stack>

            </Box>

            {/* DataGrid */}

            <Box
                sx={{
                    height: 250,
                    width: '50%'
                }}
            >

                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    disableRowSelectionOnClick

                    pageSizeOptions={[
                        10,
                        25,
                        50,
                        100
                    ]}

                    initialState={{
                        pagination: {
                            paginationModel: {
                                page: 0,
                                pageSize: 10
                            }
                        }
                    }}
                />

            </Box>

            {/* Create */}

            <PrintingProcessCreate
                open={openCreate}
                onClose={() =>
                    setOpenCreate(false)
                }
                onSuccess={handleCreateSuccess}
            />

            {/* Update */}

            <PrintingProcessUpdate
                open={openUpdate}
                id={selectedId}
                onClose={() => {
                    setOpenUpdate(false);
                    setSelectedId(null);
                }}
                onSuccess={handleUpdateSuccess}
            />

            {/* Delete */}

            <PrintingProcessDelete
                open={openDelete}
                id={selectedId}
                onClose={() => {
                    setOpenDelete(false);
                    setSelectedId(null);
                }}
                onSuccess={handleDeleteSuccess}
            />

            {/* Snackbar */}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() =>
                    setSnackbar(prev => ({
                        ...prev,
                        open: false
                    }))
                }
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
            >

                <Alert
                    severity={snackbar.severity}
                    variant="filled"
                    onClose={() =>
                        setSnackbar(prev => ({
                            ...prev,
                            open: false
                        }))
                    }
                >
                    {snackbar.message}
                </Alert>

            </Snackbar>

        </Box>
    );
};

export default PrintingProcessList;